import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import { AlertCircle } from "lucide-react";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nflDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nfl-dash.pt-br";
import DesktopNavigation, { TABS_ID } from "../components/DesktopNavigation";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { BetaNotice } from "../components/BetaNotice";
import GamesView from "../components/GamesView";
import StandingsView from "../components/StandingsView";
import TeamsView from "../components/TeamsView";
import {
  getDefaultFilters,
  getGames,
  getLeagues,
  getSeasons,
  getStandings,
  getTeams,
  getConferences,
  getDivisions
} from "../services/nflDashboardService";
import NFLOddsModal from "../NFLOddsModal";
import NFLPredictionControlBar from "../NFLPredictionControlBar";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import { NFL_HEADER_COPY } from "../constants/headerContent";
import DesktopRailNavigation from "../components/desktop/DesktopRailNavigation";
import { WeekModal, TeamModal, StatusModal } from "../components/desktop/modals";
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";
import useNFLRailFilters from "../../../../components/dashboards/mobile/hooks/useNFLRailFilters";

const DEFAULT_GAMES_PAGINATION = {
  currentPage: 1,
  perPage: 16,
  totalPages: 1
};

const DEFAULT_TEAMS_PAGINATION = {
  currentPage: 1,
  perPage: 16,
  totalPages: 1
};

const normalizeOptions = (items = []) =>
  items.map((item) =>
    typeof item === "object"
      ? { value: item.value ?? item.id ?? item.name, label: item.label ?? item.name ?? item.value }
      : { value: item, label: item }
  );

const buildGamesSignature = (filters) =>
  JSON.stringify({
    date: filters.date,
    league: filters.league,
    season: filters.season,
    country: filters.country
  });

const buildStandingsSignature = (filters) =>
  JSON.stringify({
    league: filters.league,
    season: filters.season,
    conference: filters.conference,
    division: filters.division
  });

const buildTeamsSignature = (filters) =>
  JSON.stringify({
    league: filters.league,
    season: filters.season,
    conference: filters.conference
  });

const NFLDesktopDashboard = () => {
  const [activeTab, setActiveTab] = useState(TABS_ID.GAMES);
  const [filters, setFilters] = useState(getDefaultFilters());
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gamesPagination, setGamesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [teamsPagination, setTeamsPagination] = useState(DEFAULT_TEAMS_PAGINATION);
  const [seasons] = useState(getSeasons());
  const [conferences, setConferences] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);
  const gamesSignatureRef = useRef(null);
  const standingsSignatureRef = useRef(null);
  const teamsSignatureRef = useRef(null);
  const gamesLoadedRef = useRef(false);
  const standingsLoadedRef = useRef(false);
  const teamsLoadedRef = useRef(false);

  const leagues = useMemo(() => getLeagues(), []);

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    setFilters(prev => ({
      ...prev,
      date: nextFilters.week ? '' : ensureISODate(nextFilters.date, prev.date),
      league: prev.league,
      season: nextFilters.seasonYear ? String(nextFilters.seasonYear) : prev.season,
      country: prev.country,
      conference: nextFilters.conference || '',
      division: nextFilters.division || ''
    }));

    // Reset games signature to force reload
    gamesSignatureRef.current = null;
    gamesLoadedRef.current = false;
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal,
    showTeamSheet: showTeamModal,
    setShowTeamSheet: setShowTeamModal,
    showWeekSheet: showWeekModal,
    setShowWeekSheet: setShowWeekModal,
    showStatusSheet: showStatusModal,
    setShowStatusSheet: setShowStatusModal
  } = useNFLRailFilters({
    initialDate: filters.date,
    initialTeam: null,
    onFiltersChange: handleRailFiltersChange
  });

  const {
    isLoading: isPredicting,
    selectedGames: selectedGamesForPrediction,
    usageState,
    error: predictionError,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    isGameFrozen,
    selectionCount,
    checkCachedPredictions
  } = useQuickPrediction();

  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? nflDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const loadMeta = useCallback(async () => {
    try {
      const [conf, div] = await Promise.all([
        getConferences(filters.league, filters.season),
        getDivisions(filters.league, filters.season)
      ]);
      setConferences(normalizeOptions(conf));
      setDivisions(normalizeOptions(div));
    } catch (metaError) {
      console.error("Failed to load NFL metadata:", metaError);
    }
  }, [filters.league, filters.season]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const handleFilterChange = useCallback(
    (key, value) => {
      setFilters((prev) => {
        if (key === "league") {
          return {
            ...prev,
            league: value,
            conference: "",
            division: ""
          };
        }
        return { ...prev, [key]: value };
      });

      if (activeTab === TABS_ID.GAMES) {
        setGamesPagination((prev) => ({ ...prev, currentPage: 1 }));
      } else if (activeTab === TABS_ID.TEAMS) {
        setTeamsPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    },
    [activeTab]
  );

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handleTeamsPageChange = useCallback((page) => {
    setTeamsPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("nfl");
    clearSelections(); // hide control bar immediately after request submission
    setPredictionFlowActive(false);
  }, [predictSelectedGames, clearSelections]);

  const loadGamesData = useCallback(
    async ({ force = false } = {}) => {
      const signature = buildGamesSignature(filters);
      if (!force && gamesLoadedRef.current && gamesSignatureRef.current === signature) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Merge filters with rail filters
        const requestFilters = {
          ...filters,
          teamId: railFilters.team?.id || null,
          weekId: railFilters.week || '',
          status: railFilters.status || 'all'
        };

        let gamesData = await getGames(requestFilters);
        gamesData = gamesData.filter((data) => !data?.teams?.away?.name || !data?.teams?.away?.home)

        const gamesWithIds = gamesData.map((game, index) => ({
          ...game,
          id: game.game?.id || game.id || `game-${index}`
        }));
        setGames(gamesWithIds);
        setGamesPagination((prev) => {
          const totalPages = Math.max(1, Math.ceil(gamesWithIds.length / prev.perPage));
          return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
        });
        gamesSignatureRef.current = signature;
        gamesLoadedRef.current = true;
      } catch (err) {
        console.error("Failed to load NFL games:", err);
        setError(t("Failed to load games"));
      } finally {
        setLoading(false);
      }
    },
    [filters, railFilters, t]
  );

  const loadStandingsData = useCallback(
    async ({ force = false } = {}) => {
      if (!filters.league || !filters.season) {
        setStandings([]);
        return;
      }

      const signature = buildStandingsSignature(filters);
      if (!force && standingsLoadedRef.current && standingsSignatureRef.current === signature) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const standingsData = await getStandings(filters);
        setStandings(standingsData);
        standingsSignatureRef.current = signature;
        standingsLoadedRef.current = true;
      } catch (err) {
        console.error("Failed to load NFL standings:", err);
        setError(t("Failed to load standings"));
      } finally {
        setLoading(false);
      }
    },
    [filters, t]
  );

  const loadTeamsData = useCallback(
    async ({ force = false } = {}) => {
      const signature = buildTeamsSignature(filters);
      if (!force && teamsLoadedRef.current && teamsSignatureRef.current === signature) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const teamsData = await getTeams(filters);
        setTeams(teamsData);
        setTeamsPagination((prev) => {
          const totalPages = Math.max(1, Math.ceil(teamsData.length / prev.perPage));
          return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
        });
        teamsSignatureRef.current = signature;
        teamsLoadedRef.current = true;
      } catch (err) {
        console.error("Failed to load NFL teams:", err);
        setError(t("Failed to load teams"));
      } finally {
        setLoading(false);
      }
    },
    [filters, t]
  );

  useEffect(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGamesData();
    }
  }, [activeTab, loadGamesData]);

  // Prime predictions from cache/Firestore when games change
  useEffect(() => {
    if (activeTab !== TABS_ID.GAMES) return;
    if (!games.length) return;
    const gameIds = games.map((g) => g.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("nfl", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NFL desktop):", err)
    );
  }, [activeTab, games, checkCachedPredictions]);

  // Prime predictions from cache/Firestore when games change
  useEffect(() => {
    if (activeTab !== TABS_ID.GAMES) return;
    if (!games.length) return;
    const gameIds = games.map((g) => g.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("nfl", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NFL desktop):", err)
    );
  }, [activeTab, games, checkCachedPredictions]);

  useEffect(() => {
    if (activeTab === TABS_ID.STANDINGS) {
      loadStandingsData();
    }
  }, [activeTab, loadStandingsData]);

  useEffect(() => {
    if (activeTab === TABS_ID.TEAMS) {
      loadTeamsData();
    }
  }, [activeTab, loadTeamsData]);

  // Reload games when rail filters change
  useEffect(() => {
    if (activeTab === TABS_ID.GAMES) {
      gamesLoadedRef.current = false;
      loadGamesData({ force: true });
    }
  }, [railFilters, activeTab, loadGamesData]);

  // Load teams on mount for team filter
  useEffect(() => {
    const loadInitialTeams = async () => {
      try {
        const teamsData = await getTeams({
          league: filters.league,
          season: filters.season,
          conference: ''
        });
        setTeams(teamsData);
      } catch (err) {
        console.error('Failed to load teams for filter:', err);
      }
    };
    loadInitialTeams();
  }, [filters.league, filters.season]);

  // Auto-clear selections and reset view after a prediction request finishes
  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
    }
  }, [clearSelections, isPredicting, predictionFlowActive, predictionRequestId]);

  const refreshData = useCallback(() => {
    if (activeTab === TABS_ID.GAMES) {
      loadGamesData({ force: true });
    } else if (activeTab === TABS_ID.STANDINGS) {
      loadStandingsData({ force: true });
    } else if (activeTab === TABS_ID.TEAMS) {
      loadTeamsData({ force: true });
    }
  }, [activeTab, loadGamesData, loadStandingsData, loadTeamsData]);


  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <UnifiedNavHeader title="NFL Dashboard" showBackButton />

      <div className="relative px-6 py-8 pb-24">
        <div className="max-w-[1600px] mx-auto space-y-4">
          <Header t={t} {...NFL_HEADER_COPY} />
          {/* <BetaNotice t={t} /> */}

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="nfl"
            primaryTabs={railPrimaryTabs}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          <DesktopNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

          {error && (
            <div className="border border-red-500/40 rounded-sm p-4 bg-red-500/10">
              <div className="flex items-center gap-3 text-sm font-mono uppercase tracking-widest text-red-200">
                <AlertCircle className="text-red-300" size={20} />
                <p>{error}</p>
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {activeTab === TABS_ID.GAMES && (
                <GamesView
                  games={games}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  loading={loading}
                  pagination={gamesPagination}
                  onPageChange={handleGamesPageChange}
                  t={t}
                  isGameSelected={isGameSelected}
                  isGameFrozen={isGameFrozen}
                  onToggleSelection={toggleGameSelection}
                  getGamePrediction={getGamePrediction}
                  onViewOdds={setSelectedGameForOdds}
                />
              )}

              {activeTab === TABS_ID.STANDINGS && (
                <StandingsView
                  standings={standings}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  seasons={seasons}
                  conferences={conferences}
                  divisions={divisions}
                  loading={loading}
                  onRefresh={refreshData}
                  t={t}
                />
              )}

              {activeTab === TABS_ID.TEAMS && (
                <TeamsView
                  teams={teams}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  leagues={leagues}
                  seasons={seasons}
                  conferences={conferences}
                  loading={loading}
                  onRefresh={refreshData}
                  pagination={teamsPagination}
                  onPageChange={handleTeamsPageChange}
                  t={t}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {selectedGameForOdds && (
        <NFLOddsModal game={selectedGameForOdds} onClose={() => setSelectedGameForOdds(null)} />
      )}

      {/* Desktop Filter Modals */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || filters.date)}
        onDateSelect={(isoDate) => {
          const formatted = ensureISODate(isoDate, filters.date);
          updateRailFilter("date", formatted);
          updateRailFilter("week", null);
          setShowDateModal(false);
        }}
      />

      <WeekModal
        isOpen={showWeekModal}
        onClose={() => setShowWeekModal(false)}
        currentWeek={railFilters.week}
        onWeekChange={(weekData) => {
          updateRailFilter('week', weekData.weekId);
          updateRailFilter('seasonType', weekData.seasonType);
          updateRailFilter('date', null);
          setShowWeekModal(false);
        }}
        seasonYear={railFilters.seasonYear || new Date().getFullYear()}
      />

      <TeamModal
        isOpen={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        selectedTeam={railFilters.team}
        onTeamSelect={(team) => {
          updateRailFilter('team', team);
          setShowTeamModal(false);
        }}
        teams={teams}
        sport="nfl"
      />

      <StatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={railFilters.status || 'all'}
        onStatusChange={(status) => {
          updateRailFilter('status', status);
          setShowStatusModal(false);
        }}
        sport="nfl"
      />

      <NFLPredictionControlBar
        selectionCount={selectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        error={predictionError}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        selectedGames={selectedGamesForPrediction || []}
      />
    </div>
  );
};

export default NFLDesktopDashboard;
